import { useCallback, useRef } from 'react';
import store, { showToastWithType } from '../../../../core/store';
import { updateCasePlanWidgetConfig } from '../../api/casePlanDetailApi';
import { CASE_PLAN_WIDGET_ERROR_MESSAGES } from '../../constants/reentry-planning.constants';
import { findMappedWidgetById } from '../../utils/reentry-plan-detail.util';
import { replaceChecklistWidgetProps, toChecklistWidgetConfig } from './checklistWidgetFactory';

const TOAST_TYPE_ERROR = 'error';

/**
 * Persists checklist body edits via widget config PATCH with optimistic UI:
 * apply local state immediately, PATCH in the background, revert + red toast on failure.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (updater: object[] | ((current: object[]) => object[])) => void,
 * }} params
 */
export const useChecklistWidgetMutations = ({ casePlanId, sections, setSections }) => {
  // Keeps the latest sections for async handlers after an optimistic apply (avoids stale closures).
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const applyChecklistProps = useCallback(
    (widgetId, props) => {
      const nextSections = replaceChecklistWidgetProps(sectionsRef.current, widgetId, props);
      sectionsRef.current = nextSections;
      setSections(nextSections);
    },
    [setSections],
  );

  const applyOptimisticChecklistUpdate = useCallback(
    async (widgetId, nextProps, previousProps) => {
      if (!casePlanId || !widgetId) {
        return;
      }

      applyChecklistProps(widgetId, nextProps);

      try {
        await updateCasePlanWidgetConfig(casePlanId, widgetId, toChecklistWidgetConfig(nextProps));
      } catch {
        applyChecklistProps(widgetId, previousProps);
        store.dispatch(
          showToastWithType(CASE_PLAN_WIDGET_ERROR_MESSAGES.UPDATE_CONFIG, TOAST_TYPE_ERROR),
        );
      }
    },
    [applyChecklistProps, casePlanId],
  );

  const resolveWidgetProps = useCallback(
    (widgetId) => findMappedWidgetById(sectionsRef.current, widgetId)?.props ?? null,
    [],
  );

  const mutateItems = useCallback(
    async (widgetId, transformItems) => {
      const currentProps = resolveWidgetProps(widgetId);
      if (!currentProps) {
        return;
      }

      const items = transformItems(currentProps.items ?? []);
      await applyOptimisticChecklistUpdate(widgetId, { ...currentProps, items }, currentProps);
    },
    [applyOptimisticChecklistUpdate, resolveWidgetProps],
  );

  const onTitleBlur = useCallback(
    async (widgetId, title) => {
      const currentProps = resolveWidgetProps(widgetId);
      if (!currentProps || currentProps.title === title) {
        return;
      }

      await applyOptimisticChecklistUpdate(widgetId, { ...currentProps, title }, currentProps);
    },
    [applyOptimisticChecklistUpdate, resolveWidgetProps],
  );

  const onSaveItem = useCallback(
    async (widgetId, itemId, text) => {
      await mutateItems(widgetId, (items) => [...items, { id: itemId, text, checked: false }]);
    },
    [mutateItems],
  );

  const onEditItem = useCallback(
    async (widgetId, itemId, text) => {
      await mutateItems(widgetId, (items) =>
        items.map((item) => (item.id === itemId ? { ...item, text } : item)),
      );
    },
    [mutateItems],
  );

  const onToggleItem = useCallback(
    async (widgetId, itemId, checked) => {
      await mutateItems(widgetId, (items) =>
        items.map((item) => (item.id === itemId ? { ...item, checked } : item)),
      );
    },
    [mutateItems],
  );

  const onRemoveItem = useCallback(
    async (widgetId, itemId) => {
      await mutateItems(widgetId, (items) => items.filter((item) => item.id !== itemId));
    },
    [mutateItems],
  );

  return {
    onTitleBlur,
    onSaveItem,
    onEditItem,
    onToggleItem,
    onRemoveItem,
  };
};
